// Tickets (7 October 2026): queries from every channel become tickets the business's team works through (call
// the client back, ask a question, send someone out). `services/tickets`, reached through the gateway at
// `/v1/tickets/**`. The contract is `_docs/modules/tickets-contract.md`.
//
// Hand-typed until the generated client is next fetched from a running stack. The request path is the house
// core, so the envelope, token refresh and ApiError mapping are the same as everywhere.
//
// Agents read and work tickets (reply, note, status, priority, assignee, fields, tags, tasks, merge, link). Writes
// to settings, types, statuses, priorities, SLA policies and rules refuse them (403). With visibility `team`, an
// agent sees tickets of their teams, tickets assigned to them and tickets with no team.

import { http } from './_core/request.js';
import type { Envelope, Meta } from './_core/envelope.js';
import { isApiError } from './_core/errors.js';

// ── Error codes ────────────────────────────────────────────────────────────

/** 409: the plan allows no more ticket types (on create) or active automations (on create or switching one on). */
export const PLAN_LIMIT_REACHED = 'PLAN_LIMIT_REACHED';
/** 409: a status (or priority) that tickets still use cannot be deleted. */
export const IN_USE = 'IN_USE';
/** 409, passed through from the inbox: the channel's messaging window is closed; send a template instead. */
export const MESSAGING_WINDOW_CLOSED = 'MESSAGING_WINDOW_CLOSED';
/** 409: the ticket has no linked conversation to reply on. */
export const NO_CONVERSATION = 'NO_CONVERSATION';

export const ERROR_CODES = [PLAN_LIMIT_REACHED, IN_USE, MESSAGING_WINDOW_CLOSED, NO_CONVERSATION] as const;
export type TicketErrorCode = (typeof ERROR_CODES)[number];

/** The tickets error code an error carries, or null when it is not one of ours. */
export function ticketErrorCode(e: unknown): TicketErrorCode | null {
  if (!isApiError(e)) return null;
  return (ERROR_CODES as readonly string[]).includes(e.code) ? (e.code as TicketErrorCode) : null;
}

// ── Lookups ────────────────────────────────────────────────────────────────

export type StatusCategory = 'new' | 'open' | 'pending' | 'on_hold' | 'solved' | 'closed';
export const STATUS_CATEGORIES: readonly StatusCategory[] = ['new', 'open', 'pending', 'on_hold', 'solved', 'closed'];

export type Visibility = 'all' | 'team';
export type FieldType = 'text' | 'number' | 'date' | 'select' | 'multi_select' | 'yes_no' | 'address';
export const FIELD_TYPES: readonly FieldType[] = ['text', 'number', 'date', 'select', 'multi_select', 'yes_no', 'address'];

export type TicketView = 'all' | 'mine' | 'team' | 'unassigned' | 'breaching';
export const TICKET_VIEWS: readonly TicketView[] = ['all', 'mine', 'team', 'unassigned', 'breaching'];

export type TaskKind = 'call' | 'question' | 'callout' | 'custom';
export const TASK_KINDS: readonly TaskKind[] = ['call', 'question', 'callout', 'custom'];

export type ActivityKind =
  | 'reply' | 'client_message' | 'note' | 'status' | 'priority' | 'assignee' | 'team' | 'field' | 'task'
  | 'system' | 'csat' | 'merge' | 'link';

/** Where a ticket came from: a person in the console, a flow step, a voice call or an automation. */
export type TicketSource = 'console' | 'flow' | 'voice' | 'rule' | string;

// ── Settings ───────────────────────────────────────────────────────────────

export interface TicketLimits {
  /** Ticket types the plan allows; null means no limit. */
  ticket_types: number | null;
  /** Active automations the plan allows; null means no limit. */
  ticket_automations: number | null;
}

export interface NotifyClient {
  /** "We have your request {number}." when a ticket is opened. */
  created: boolean;
  /** The new status's client label on every status change. */
  status_changed: boolean;
  /** The solved status's client label on solve. */
  solved: boolean;
}

export interface Settings {
  /** Ticket numbers start with this, such as `TK`. */
  number_prefix: string;
  visibility: Visibility;
  notify_client: NotifyClient;
  csat_enabled: boolean;
  /** null or empty uses the default question. */
  csat_question: string | null;
  /** Solved tickets close after this many days; null or 0 never. */
  auto_close_days: number | null;
  /** A client message on a solved ticket reopens it. */
  reopen_on_reply: boolean;
  /** Read only: from the plan. */
  limits?: TicketLimits;
  updated_at?: string;
}

export type SettingsUpdate = Omit<Settings, 'limits' | 'updated_at'>;

// ── Types and fields ───────────────────────────────────────────────────────

export interface TypeField {
  /** Stable key the value is stored under. */
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  /** For `select` and `multi_select`. */
  options?: string[];
  /** Whether the client may be told this value. */
  client_visible: boolean;
}

export interface TicketType {
  id: string;
  name: string;
  description: string | null;
  /** A CSS colour such as `#3b82f6`. */
  colour: string | null;
  fields: TypeField[];
  default_team_id: string | null;
  default_priority_id: string | null;
  sort_order: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type TicketTypeInput = Omit<TicketType, 'id' | 'created_at' | 'updated_at'>;

export interface Status {
  id: string;
  /** null: the status applies to every type. */
  type_id: string | null;
  name: string;
  category: StatusCategory;
  /** What the client is told, such as "We are working on it". */
  client_label: string | null;
  sort_order: number;
  is_default: boolean;
}

export type StatusInput = Omit<Status, 'id'>;

export interface Priority {
  id: string;
  name: string;
  colour: string | null;
  /** Higher is more urgent. */
  rank: number;
  is_default: boolean;
}

export type PriorityInput = Omit<Priority, 'id'>;

export interface SlaPolicy {
  id: string;
  name: string;
  /** null matches every type. */
  type_id: string | null;
  /** null matches every priority. */
  priority_id: string | null;
  first_response_minutes: number | null;
  resolution_minutes: number | null;
  /** Count only minutes inside the organisation's hours, outside closures. */
  business_hours_only: boolean;
  sort_order: number;
  active: boolean;
}

export type SlaPolicyInput = Omit<SlaPolicy, 'id'>;

// ── Automations ────────────────────────────────────────────────────────────

export type RuleTrigger = 'message_received' | 'unanswered' | 'ticket_created' | 'ticket_updated' | 'time_passed';
export const RULE_TRIGGERS: readonly RuleTrigger[] = ['message_received', 'unanswered', 'ticket_created', 'ticket_updated', 'time_passed'];

/**
 * `channel`, `text`, `contact.tag`, `business_hours` (`open|closed`), `ticket.type_id`, `ticket.priority_id`,
 * `ticket.status_category`, `ticket.team_id`, `ticket.tag`, `ticket.field.<key>`, `minutes_in_status`, `minutes`.
 */
export type ConditionField = string;
export type ConditionOp = 'eq' | 'neq' | 'in' | 'contains_any' | 'not_contains_any' | 'is_set' | 'not_set' | 'gte' | 'lte';
export const CONDITION_OPS: readonly ConditionOp[] = ['eq', 'neq', 'in', 'contains_any', 'not_contains_any', 'is_set', 'not_set', 'gte', 'lte'];

export interface Condition {
  field: ConditionField;
  op: ConditionOp;
  /** A string or number; an array for `in`, `contains_any` and `not_contains_any`; absent for `is_set` and `not_set`. */
  value?: string | number | boolean | string[] | null;
}

export type ActionType =
  | 'create_ticket' | 'assign' | 'set_status' | 'set_priority' | 'add_tag' | 'set_field' | 'notify'
  | 'reply_client' | 'add_task' | 'close';
export const ACTION_TYPES: readonly ActionType[] = [
  'create_ticket', 'assign', 'set_status', 'set_priority', 'add_tag', 'set_field', 'notify', 'reply_client', 'add_task', 'close'
];

export type RuleAction =
  /** Only for `message_received` and `unanswered`; nothing happens if the conversation already has an open ticket. */
  | { type: 'create_ticket'; type_id?: string | null; priority_id?: string | null; team_id?: string | null; subject?: string | null }
  | { type: 'assign'; team_id?: string | null; user_id?: string | null; round_robin?: boolean }
  | { type: 'set_status'; status_id: string }
  | { type: 'set_priority'; priority_id: string }
  | { type: 'add_tag'; tag: string }
  | { type: 'set_field'; key: string; value: unknown }
  | { type: 'notify'; to: 'team' | 'assignee' | 'user'; user_id?: string | null }
  | { type: 'reply_client'; text: string }
  | { type: 'add_task'; kind: TaskKind; title: string; due_minutes?: number | null }
  | { type: 'close' };

export interface Rule {
  id: string;
  name: string;
  trigger: RuleTrigger;
  conditions: Condition[];
  actions: RuleAction[];
  sort_order: number;
  /** Stop checking later rules once this one has run. */
  stop_after: boolean;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type RuleInput = Omit<Rule, 'id' | 'created_at' | 'updated_at'>;

/**
 * One time a rule ran. ⚠ The contract names `RuleRun` without a shape; these are the fields the console reads,
 * all optional beyond `id` and `at`, so a service answering a little more or less still renders.
 */
export interface RuleRun {
  id: string;
  rule_id?: string;
  at: string;
  ticket_id?: string | null;
  ticket_number?: string | null;
  conversation_id?: string | null;
  /** Such as `done`, `skipped` or `failed`. */
  outcome?: string | null;
  /** What it did or why it did not, in words. */
  detail?: string | null;
}

// ── Tickets ────────────────────────────────────────────────────────────────

export interface TicketContact {
  id: string | null;
  ref: string | null;
  name: string | null;
}

export interface TicketSla {
  first_response_due: string | null;
  resolution_due: string | null;
  breached: boolean;
  /** The clock is stopped while the ticket is pending or on hold. */
  paused: boolean;
}

export interface TicketSummary {
  id: string;
  /** What the client quotes, such as `TK-1042`. */
  number: string;
  subject: string;
  type: { id: string; name: string; colour: string | null };
  status: { id: string; name: string; category: StatusCategory };
  priority: { id: string; name: string; colour: string | null; rank: number };
  team_id: string | null;
  assignee_user_id: string | null;
  contact: TicketContact;
  channel: string | null;
  tags: string[];
  due_at: string | null;
  sla: TicketSla | null;
  created_at: string;
  updated_at: string;
}

export interface Activity {
  id: string;
  kind: ActivityKind | string;
  /** A person's name, "Client", "Automation: <rule name>" or "System". */
  actor: string | null;
  body: string | null;
  detail: Record<string, unknown> | string | null;
  at: string;
}

export interface Task {
  id: string;
  ticket_id: string;
  ticket_number: string | null;
  ticket_subject: string | null;
  kind: TaskKind;
  title: string;
  due_at: string | null;
  assignee_user_id: string | null;
  booking_id: string | null;
  question: string | null;
  done_at: string | null;
  done_by: string | null;
  outcome: string | null;
  created_at: string;
}

export interface TicketConversation {
  id: string;
  channel: string | null;
}

export interface Ticket extends TicketSummary {
  description: string | null;
  fields: Record<string, unknown> | null;
  source: TicketSource | null;
  first_response_at: string | null;
  solved_at: string | null;
  closed_at: string | null;
  /** 1 to 5, when the client answered the satisfaction question. */
  csat_score: number | null;
  conversations: TicketConversation[];
  activities: Activity[];
  tasks: Task[];
  /** Set when this ticket was merged into another. */
  merged_into_id: string | null;
}

export type TicketCounts = Record<StatusCategory, number>;

export interface TicketListMeta extends Meta {
  next_cursor?: string | null;
  counts?: Partial<TicketCounts>;
}

export interface TicketList {
  data: TicketSummary[];
  meta?: TicketListMeta;
}

export interface TicketListQuery {
  view?: TicketView;
  status_id?: string;
  category?: StatusCategory;
  type_id?: string;
  priority_id?: string;
  team_id?: string;
  assignee_user_id?: string;
  q?: string;
  tag?: string;
  conversation_id?: string;
  contact_ref?: string;
  cursor?: string;
  limit?: number;
}

export interface CreateTicketRequest {
  subject: string;
  description?: string;
  type_id?: string;
  priority_id?: string;
  team_id?: string;
  assignee_user_id?: string;
  contact?: { id?: string; ref?: string; name?: string };
  conversation_id?: string;
  channel?: string;
  fields?: Record<string, unknown>;
  tags?: string[];
  /** Default: the settings' `notify_client.created`. */
  notify_client?: boolean;
}

/** Only the fields sent change; `null` clears team, assignee or due time. */
export interface UpdateTicketRequest {
  subject?: string;
  description?: string | null;
  type_id?: string;
  status_id?: string;
  priority_id?: string;
  team_id?: string | null;
  assignee_user_id?: string | null;
  fields?: Record<string, unknown>;
  tags?: string[];
  due_at?: string | null;
}

export interface TaskBookingRequest {
  service_id: string;
  starts_at: string;
  staff_id?: string;
  address?: string;
}

export interface CreateTaskRequest {
  kind: TaskKind;
  title: string;
  due_at?: string;
  assignee_user_id?: string;
  /** For `question`: sent to the client, and the ticket moves to its pending status. */
  question?: string;
  /** For `callout`: booked through bookings with the ticket's contact. */
  booking?: TaskBookingRequest;
}

export interface UpdateTaskRequest {
  title?: string;
  due_at?: string | null;
  assignee_user_id?: string | null;
  done?: boolean;
  outcome?: string;
}

export interface TaskListQuery {
  mine?: boolean;
  due_before?: string;
  open?: boolean;
}

// ── Reports ────────────────────────────────────────────────────────────────

export interface ReportDay {
  /** `yyyy-MM-dd`. */
  date: string;
  created: number;
  solved: number;
}

export interface ReportTeamRow {
  team_id: string | null;
  created: number;
  solved: number;
  sla_met_percent: number | null;
}

export interface ReportAssigneeRow {
  user_id: string | null;
  solved: number;
  first_response_minutes_avg: number | null;
  csat_avg: number | null;
}

export interface Report {
  created: number;
  solved: number;
  open_now_by_category: Partial<TicketCounts>;
  first_response_minutes_avg: number | null;
  resolution_minutes_avg: number | null;
  sla_met_percent: number | null;
  csat_avg: number | null;
  csat_count: number;
  by_day: ReportDay[];
  by_team: ReportTeamRow[];
  by_assignee: ReportAssigneeRow[];
}

export interface ReportQuery {
  /** `yyyy-MM-dd` or ISO. */
  from: string;
  to: string;
  team_id?: string;
}

// ── Calls ──────────────────────────────────────────────────────────────────

const enc = encodeURIComponent;

export function getSettings(): Promise<Envelope<Settings>> {
  return http.get<Envelope<Settings>>('/v1/tickets/settings');
}

/** Owners and admins. */
export function saveSettings(req: SettingsUpdate): Promise<Envelope<Settings>> {
  return http.put<Envelope<Settings>>('/v1/tickets/settings', { body: req });
}

export function listTypes(): Promise<Envelope<TicketType[]>> {
  return http.get<Envelope<TicketType[]>>('/v1/tickets/types');
}

/** Owners and admins. 409 `PLAN_LIMIT_REACHED` past the plan's `ticket_types`. */
export function createType(req: Partial<TicketTypeInput> & { name: string }): Promise<Envelope<TicketType>> {
  return http.post<Envelope<TicketType>>('/v1/tickets/types', { body: req });
}

export function updateType(id: string, req: Partial<TicketTypeInput>): Promise<Envelope<TicketType>> {
  return http.patch<Envelope<TicketType>>(`/v1/tickets/types/${enc(id)}`, { body: req });
}

/** Soft: the type stops being offered, its tickets keep it. */
export function deleteType(id: string): Promise<void> {
  return http.delete<void>(`/v1/tickets/types/${enc(id)}`);
}

export function listStatuses(): Promise<Envelope<Status[]>> {
  return http.get<Envelope<Status[]>>('/v1/tickets/statuses');
}

export function createStatus(req: Partial<StatusInput> & { name: string; category: StatusCategory }): Promise<Envelope<Status>> {
  return http.post<Envelope<Status>>('/v1/tickets/statuses', { body: req });
}

export function updateStatus(id: string, req: Partial<StatusInput>): Promise<Envelope<Status>> {
  return http.patch<Envelope<Status>>(`/v1/tickets/statuses/${enc(id)}`, { body: req });
}

/** 409 `IN_USE` while tickets have this status. */
export function deleteStatus(id: string): Promise<void> {
  return http.delete<void>(`/v1/tickets/statuses/${enc(id)}`);
}

export function listPriorities(): Promise<Envelope<Priority[]>> {
  return http.get<Envelope<Priority[]>>('/v1/tickets/priorities');
}

export function createPriority(req: Partial<PriorityInput> & { name: string }): Promise<Envelope<Priority>> {
  return http.post<Envelope<Priority>>('/v1/tickets/priorities', { body: req });
}

export function updatePriority(id: string, req: Partial<PriorityInput>): Promise<Envelope<Priority>> {
  return http.patch<Envelope<Priority>>(`/v1/tickets/priorities/${enc(id)}`, { body: req });
}

/** 409 `IN_USE` while tickets have this priority. */
export function deletePriority(id: string): Promise<void> {
  return http.delete<void>(`/v1/tickets/priorities/${enc(id)}`);
}

export function listSlaPolicies(): Promise<Envelope<SlaPolicy[]>> {
  return http.get<Envelope<SlaPolicy[]>>('/v1/tickets/sla-policies');
}

export function createSlaPolicy(req: Partial<SlaPolicyInput> & { name: string }): Promise<Envelope<SlaPolicy>> {
  return http.post<Envelope<SlaPolicy>>('/v1/tickets/sla-policies', { body: req });
}

export function updateSlaPolicy(id: string, req: Partial<SlaPolicyInput>): Promise<Envelope<SlaPolicy>> {
  return http.patch<Envelope<SlaPolicy>>(`/v1/tickets/sla-policies/${enc(id)}`, { body: req });
}

export function deleteSlaPolicy(id: string): Promise<void> {
  return http.delete<void>(`/v1/tickets/sla-policies/${enc(id)}`);
}

export function listRules(): Promise<Envelope<Rule[]>> {
  return http.get<Envelope<Rule[]>>('/v1/tickets/rules');
}

/** 409 `PLAN_LIMIT_REACHED` when an active rule would pass the plan's `ticket_automations`. */
export function createRule(req: Partial<RuleInput> & { name: string; trigger: RuleTrigger }): Promise<Envelope<Rule>> {
  return http.post<Envelope<Rule>>('/v1/tickets/rules', { body: req });
}

/** 409 `PLAN_LIMIT_REACHED` when switching a rule on would pass the plan's `ticket_automations`. */
export function updateRule(id: string, req: Partial<RuleInput>): Promise<Envelope<Rule>> {
  return http.patch<Envelope<Rule>>(`/v1/tickets/rules/${enc(id)}`, { body: req });
}

export function deleteRule(id: string): Promise<void> {
  return http.delete<void>(`/v1/tickets/rules/${enc(id)}`);
}

/** The rules in the order given; answers every rule. */
export function reorderRules(ids: string[]): Promise<Envelope<Rule[]>> {
  return http.post<Envelope<Rule[]>>('/v1/tickets/rules/reorder', { body: { ids } });
}

export function listRuleRuns(id: string, opts?: { limit?: number }): Promise<Envelope<RuleRun[]>> {
  return http.get<Envelope<RuleRun[]>>(`/v1/tickets/rules/${enc(id)}/runs`, { query: { limit: opts?.limit } });
}

export function listTickets(q?: TicketListQuery): Promise<TicketList> {
  return http.get<TicketList>('/v1/tickets', {
    query: q ? {
      view: q.view, status_id: q.status_id, category: q.category, type_id: q.type_id, priority_id: q.priority_id,
      team_id: q.team_id, assignee_user_id: q.assignee_user_id, q: q.q, tag: q.tag,
      conversation_id: q.conversation_id, contact_ref: q.contact_ref, cursor: q.cursor, limit: q.limit
    } : undefined
  });
}

export function createTicket(req: CreateTicketRequest): Promise<Envelope<Ticket>> {
  return http.post<Envelope<Ticket>>('/v1/tickets', { body: req });
}

/** With its activities, tasks, conversations and SLA. */
export function getTicket(id: string): Promise<Envelope<Ticket>> {
  return http.get<Envelope<Ticket>>(`/v1/tickets/${enc(id)}`);
}

export function updateTicket(id: string, req: UpdateTicketRequest): Promise<Envelope<Ticket>> {
  return http.patch<Envelope<Ticket>>(`/v1/tickets/${enc(id)}`, { body: req });
}

/**
 * To the client, on `conversation_id` or the most recent linked conversation. 409 `MESSAGING_WINDOW_CLOSED` (send a
 * template from the inbox instead) or `NO_CONVERSATION`.
 */
export function replyToTicket(id: string, req: { text: string; conversation_id?: string }): Promise<Envelope<Activity>> {
  return http.post<Envelope<Activity>>(`/v1/tickets/${enc(id)}/reply`, { body: req });
}

/** Internal: the client never sees a note. */
export function addTicketNote(id: string, text: string): Promise<Envelope<Activity>> {
  return http.post<Envelope<Activity>>(`/v1/tickets/${enc(id)}/notes`, { body: { text } });
}

export function linkConversation(id: string, conversationId: string): Promise<Envelope<Ticket>> {
  return http.post<Envelope<Ticket>>(`/v1/tickets/${enc(id)}/link`, { body: { conversation_id: conversationId } });
}

/** Answers the ticket merged into. */
export function mergeTicket(id: string, intoTicketId: string): Promise<Envelope<Ticket>> {
  return http.post<Envelope<Ticket>>(`/v1/tickets/${enc(id)}/merge`, { body: { into_ticket_id: intoTicketId } });
}

export function createTask(ticketId: string, req: CreateTaskRequest): Promise<Envelope<Task>> {
  return http.post<Envelope<Task>>(`/v1/tickets/${enc(ticketId)}/tasks`, { body: req });
}

export function updateTask(ticketId: string, taskId: string, req: UpdateTaskRequest): Promise<Envelope<Task>> {
  return http.patch<Envelope<Task>>(`/v1/tickets/${enc(ticketId)}/tasks/${enc(taskId)}`, { body: req });
}

/** Tasks across tickets, each with its ticket's number and subject. */
export function listTasks(q?: TaskListQuery): Promise<Envelope<Task[]>> {
  return http.get<Envelope<Task[]>>('/v1/tickets/tasks', {
    query: q ? { mine: q.mine, due_before: q.due_before, open: q.open } : undefined
  });
}

export function getReport(q: ReportQuery): Promise<Envelope<Report>> {
  return http.get<Envelope<Report>>('/v1/tickets/reports', { query: { from: q.from, to: q.to, team_id: q.team_id } });
}
